import { isAnyOf } from "@reduxjs/toolkit";
import { QueryStatus } from "@reduxjs/toolkit/query";

import type { Product } from "@jti/magento";
import { getSessionStorage } from "@jti/rest-api";
import type { GetEligibleCampaignsResponseType } from "@jti/rest-api-types";

import { startListening } from "../middlewares/listenerMiddleware";
import { checkoutV2Slice } from "../slices/checkoutV2Slice/checkoutV2Slice";
import { CheckoutV2LoyaltyChoice } from "../slices/checkoutV2Slice/checkoutV2Slice.types";
import { loyaltyApiSlice } from "../slices/loyaltyApiSlice";
import { magentoApiSlice } from "../slices/magentoApiSlice";
import { TAGS } from "../slices/magentoApiSlice/common/tags";
import type { RootStoreState } from "../types";

const selectCartId = (state: RootStoreState): string | undefined =>
  magentoApiSlice.endpoints.getCustomerCartId.select()(state)?.data?.customerCart?.id;

const selectCartItems = (state: RootStoreState, cartId?: string): Product[] => {
  if (!cartId) {
    return [];
  }

  return magentoApiSlice.endpoints.getCartItems.select({ cartId })(state)?.data?.items ?? [];
};

const selectCartItemsQuery = (state: RootStoreState, cartId?: string) => {
  if (!cartId) {
    return undefined;
  }

  return magentoApiSlice.endpoints.getCartItems.select({ cartId })(state);
};

const selectCartSubtotal = (state: RootStoreState, cartId?: string): number | undefined =>
  cartId
    ? (magentoApiSlice.endpoints.getCustomerCartOrderPrices.select(cartId)(state)?.data?.subtotalIncludingTax?.value ??
      undefined)
    : undefined;

const selectAppliedCampaign = (state: RootStoreState, cartId?: string) =>
  cartId
    ? magentoApiSlice.endpoints.getCustomerCartOrderPrices.select(cartId)(state)?.data?.appliedCampaign
    : undefined;

const getSelectedVoucher = (vouchers: GetEligibleCampaignsResponseType[], campaignId?: string) =>
  campaignId ? vouchers.find((voucher) => voucher.campaign_id === campaignId) : undefined;

const findVoucherPrice = (vouchers: GetEligibleCampaignsResponseType[], cartSubtotal: number): number => {
  const voucher = vouchers.find((item) => item.price >= cartSubtotal);
  return voucher?.price ?? cartSubtotal;
};

const clearStoredSelection = (cartItems: Product[], hasQueryData: boolean): void => {
  if (hasQueryData && cartItems.length === 0) {
    getSessionStorage().delete("loyaltyChoice");
  }
};

const persistUserSelection = (choice: CheckoutV2LoyaltyChoice, cartId: string): void => {
  getSessionStorage().set("loyaltyChoice", {
    loyaltyChoice: choice,
    cartId,
  });
};

export type CampaignContext = {
  cartId?: string;
  cartItems: Product[];
  hasCartItemsData: boolean;
  cartSubtotal: number | undefined;
  vouchers: GetEligibleCampaignsResponseType[];
  points: number;
  cartCampaignId?: string;
  loyaltyCampaignId: string;
  effectiveCampaignId?: string;
};

type ApplyCampaignArgs = {
  cartId: string;
  campaign_uuid: string;
};

type PendingMutationRecord = {
  endpointName?: unknown;
  status?: unknown;
  originalArgs?: unknown;
};

type PendingApplyCampaignArgsRecord = {
  cartId?: unknown;
  campaign_uuid?: unknown;
};

type LoyaltyApiMutationState = {
  mutations?: Record<string, unknown>;
};

const APPLY_LOYALTY_CAMPAIGN_ENDPOINT = "applyLoyaltyCampaign";

const isPendingApplyCampaignForArgs = (mutation: unknown, args: ApplyCampaignArgs): boolean => {
  if (!mutation || typeof mutation !== "object") {
    return false;
  }

  const mutationRecord = mutation as PendingMutationRecord;

  if (
    mutationRecord.endpointName !== APPLY_LOYALTY_CAMPAIGN_ENDPOINT ||
    mutationRecord.status !== QueryStatus.pending
  ) {
    return false;
  }

  if (!mutationRecord.originalArgs || typeof mutationRecord.originalArgs !== "object") {
    return false;
  }

  const mutationArgs = mutationRecord.originalArgs as PendingApplyCampaignArgsRecord;

  return mutationArgs.cartId === args.cartId && mutationArgs.campaign_uuid === args.campaign_uuid;
};

const hasPendingApplyCampaignRequest = (state: RootStoreState, args: ApplyCampaignArgs): boolean => {
  const loyaltyApiState = state[loyaltyApiSlice.reducerPath as keyof RootStoreState] as
    LoyaltyApiMutationState | undefined;

  if (!loyaltyApiState?.mutations) {
    return false;
  }

  return Object.values(loyaltyApiState.mutations).some((mutation) => isPendingApplyCampaignForArgs(mutation, args));
};

const getCampaignContext = (state: RootStoreState): CampaignContext => {
  const cartId = selectCartId(state);
  const cartItemsQuery = selectCartItemsQuery(state, cartId);
  const cartItems = selectCartItems(state, cartId);
  const hasCartItemsData = cartItemsQuery?.data !== undefined;
  const cartSubtotal = selectCartSubtotal(state, cartId);
  const appliedCampaign = selectAppliedCampaign(state, cartId);
  const cartCampaignId = appliedCampaign?.loyaltyDiscountUuid ?? undefined;
  const { loyaltyCampaignId } = state.checkoutV2.loyaltyPointsState;

  return {
    cartId,
    cartItems,
    hasCartItemsData,
    cartSubtotal,
    vouchers: cachedVouchers,
    points: cachedUserPoints,
    cartCampaignId,
    loyaltyCampaignId,
    effectiveCampaignId: cartCampaignId || loyaltyCampaignId || undefined,
  };
};

type DispatchFn = (action: unknown) => unknown;

type LoyaltyPointsState = RootStoreState["checkoutV2"]["loyaltyPointsState"];

export const resetLoyaltyState = (state: RootStoreState, dispatch: DispatchFn): void => {
  const { loyaltyCampaignId, isLoyaltyError } = state.checkoutV2.loyaltyPointsState;

  if (!loyaltyCampaignId && !isLoyaltyError) {
    return;
  }

  if (loyaltyCampaignId) {
    dispatch(checkoutV2Slice.actions.setLoyaltyCampaignId(""));
  }

  if (isLoyaltyError) {
    dispatch(checkoutV2Slice.actions.setLoyaltyError(false));
  }

  dispatch(checkoutV2Slice.actions.setLoyaltyChoice(undefined));
  getSessionStorage().delete("loyaltyChoice");
};

let cachedVouchers: GetEligibleCampaignsResponseType[] = [];
let cachedUserPoints = 0;

export const evaluateLoyaltyCampaign = (
  context: CampaignContext,
  loyaltyState: LoyaltyPointsState,
  dispatch: DispatchFn
): void => {
  const { cartId } = context;
  if (!cartId) {
    return;
  }

  const { loyaltyCampaignId, isLoyaltyError } = loyaltyState;

  if (context.cartCampaignId && !loyaltyCampaignId && loyaltyState.loyaltyChoice !== CheckoutV2LoyaltyChoice.COLLECT) {
    dispatch(checkoutV2Slice.actions.setLoyaltyCampaignId(context.cartCampaignId));

    const restoredVoucher = getSelectedVoucher(context.vouchers, context.cartCampaignId);
    if (restoredVoucher) {
      dispatch(checkoutV2Slice.actions.setLoyaltyVoucher(restoredVoucher));
    }
  }

  if (loyaltyState.loyaltyCampaignId && !loyaltyState.loyaltyVoucher) {
    const voucher = getSelectedVoucher(context.vouchers, loyaltyState.loyaltyCampaignId);
    if (voucher) {
      dispatch(checkoutV2Slice.actions.setLoyaltyVoucher(voucher));
    }
  }

  const effectiveCampaignId = context.effectiveCampaignId;

  if (!effectiveCampaignId) {
    clearStoredSelection(context.cartItems, context.hasCartItemsData);
    return;
  }

  const selectedVoucher = getSelectedVoucher(context.vouchers, effectiveCampaignId);
  if (!selectedVoucher) {
    clearStoredSelection(context.cartItems, context.hasCartItemsData);
    return;
  }

  const highestAvailableVoucher =
    context.cartSubtotal === undefined ? 0 : findVoucherPrice(context.vouchers, context.cartSubtotal);

  const shouldRemove = selectedVoucher.points > context.points || selectedVoucher.price > highestAvailableVoucher;

  if (shouldRemove) {
    if (!isLoyaltyError) {
      dispatch(checkoutV2Slice.actions.setLoyaltyError(true));
    }

    dispatch(loyaltyApiSlice.endpoints.removeLoyaltyCampaign.initiate({ cartId }));
  }

  clearStoredSelection(context.cartItems, context.hasCartItemsData);
};

export const applyRestoredRedeemCampaign = (
  context: CampaignContext,
  loyaltyState: LoyaltyPointsState,
  dispatch: DispatchFn,
  isApplyCampaignPending = false
): void => {
  const { cartId, cartCampaignId, loyaltyCampaignId, vouchers } = context;

  if (!cartId) {
    return;
  }

  if (loyaltyState.loyaltyChoice !== CheckoutV2LoyaltyChoice.REDEEM) {
    return;
  }

  const campaignIdToApply = loyaltyCampaignId || cartCampaignId;

  if (!campaignIdToApply) {
    return;
  }

  if (cartCampaignId === campaignIdToApply) {
    return;
  }

  if (loyaltyState.isLoyaltyError) {
    return;
  }

  const selectedVoucher = getSelectedVoucher(vouchers, campaignIdToApply);

  if (vouchers.length > 0 && !selectedVoucher?.is_eligible) {
    return;
  }

  if (isApplyCampaignPending) {
    return;
  }

  dispatch(loyaltyApiSlice.endpoints.applyLoyaltyCampaign.initiate({ cartId, campaign_uuid: campaignIdToApply }));
};

export const handleCartItemsWithCampaign = (state: RootStoreState, cartId: string, dispatch: DispatchFn): void => {
  const { loyaltyCampaignId, loyaltyChoice } = state.checkoutV2.loyaltyPointsState;

  if (loyaltyCampaignId && loyaltyChoice === CheckoutV2LoyaltyChoice.COLLECT) {
    dispatch(checkoutV2Slice.actions.setLoyaltyChoice(CheckoutV2LoyaltyChoice.REDEEM));
    persistUserSelection(CheckoutV2LoyaltyChoice.REDEEM, cartId);
  }
};

export const enforceVoucherEligibilityAndCartNotEmpty = (context: CampaignContext, dispatch: DispatchFn): void => {
  const { cartId, effectiveCampaignId } = context;

  if (!cartId) {
    return;
  }

  if (!effectiveCampaignId) {
    clearStoredSelection(context.cartItems, context.hasCartItemsData);
    return;
  }

  const cartItems = context.cartItems;
  const itemsCount = cartItems.reduce((count, item) => count + (item.quantity ?? 0), 0);
  const selectedVoucher = getSelectedVoucher(context.vouchers, effectiveCampaignId);

  const isVoucherInvalid = !selectedVoucher?.is_eligible;
  const isCartEmpty = itemsCount === 0;

  if (isVoucherInvalid || isCartEmpty) {
    dispatch(loyaltyApiSlice.endpoints.removeLoyaltyCampaign.initiate({ cartId }));
    if (isCartEmpty) {
      clearStoredSelection(cartItems, context.hasCartItemsData);
    }
  }
};

startListening({
  matcher: loyaltyApiSlice.endpoints.getLoyaltyVouchers.matchFulfilled,
  effect: (action) => {
    cachedVouchers = action.payload ?? [];
  },
});

startListening({
  matcher: loyaltyApiSlice.endpoints.getLoyaltyPoints.matchFulfilled,
  effect: (action) => {
    cachedUserPoints = action.payload?.points ?? 0;
  },
});

startListening({
  matcher: isAnyOf(
    checkoutV2Slice.actions.setLoyaltyChoice,
    checkoutV2Slice.actions.setLoyaltyCampaignId,
    magentoApiSlice.endpoints.getCartItems.matchFulfilled,
    magentoApiSlice.endpoints.getCustomerCartOrderPrices.matchFulfilled,
    loyaltyApiSlice.endpoints.getLoyaltyVouchers.matchFulfilled,
    loyaltyApiSlice.endpoints.getLoyaltyPoints.matchFulfilled
  ),
  effect: (_, listenerApi) => {
    const state = listenerApi.getState();
    const context = getCampaignContext(state);
    const { cartId } = context;
    if (!cartId) {
      resetLoyaltyState(state, listenerApi.dispatch);
      return;
    }

    evaluateLoyaltyCampaign(context, state.checkoutV2.loyaltyPointsState, listenerApi.dispatch);

    const campaignIdToApply = context.loyaltyCampaignId || context.cartCampaignId;
    const isApplyCampaignPending = campaignIdToApply
      ? hasPendingApplyCampaignRequest(state, { cartId, campaign_uuid: campaignIdToApply })
      : false;

    applyRestoredRedeemCampaign(
      context,
      state.checkoutV2.loyaltyPointsState,
      listenerApi.dispatch,
      isApplyCampaignPending
    );
  },
});

startListening({
  matcher: magentoApiSlice.endpoints.getCartItems.matchFulfilled,
  effect: (action, listenerApi) => {
    const { cartId } = action.meta.arg.originalArgs;
    if (!cartId) {
      return;
    }

    handleCartItemsWithCampaign(listenerApi.getState(), cartId, listenerApi.dispatch);
  },
});

startListening({
  matcher: isAnyOf(
    checkoutV2Slice.actions.setLoyaltyCampaignId,
    magentoApiSlice.endpoints.getCartItems.matchFulfilled,
    loyaltyApiSlice.endpoints.getLoyaltyVouchers.matchFulfilled
  ),
  effect: (_, listenerApi) => {
    const state = listenerApi.getState();
    const context = getCampaignContext(state);
    const { cartId } = context;

    if (!cartId) {
      resetLoyaltyState(state, listenerApi.dispatch);
      return;
    }

    enforceVoucherEligibilityAndCartNotEmpty(context, listenerApi.dispatch);
  },
});

startListening({
  matcher: loyaltyApiSlice.endpoints.removeLoyaltyCampaign.matchFulfilled,
  effect: (_, listenerApi) => {
    listenerApi.dispatch(magentoApiSlice.util.invalidateTags([TAGS.CART_ORDER_PRICES]));
    listenerApi.dispatch(magentoApiSlice.util.invalidateTags([TAGS.AVAILABLE_PAYMENT_METHODS]));
  },
});

startListening({
  matcher: loyaltyApiSlice.endpoints.applyLoyaltyCampaign.matchFulfilled,
  effect: (_, listenerApi) => {
    listenerApi.dispatch(magentoApiSlice.util.invalidateTags([TAGS.CART_ORDER_PRICES]));
    listenerApi.dispatch(magentoApiSlice.util.invalidateTags([TAGS.AVAILABLE_PAYMENT_METHODS]));
  },
});
